import React, { useContext, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useGetUserQuarriesForClaimQuery } from '@api/queries';
import {
  useClaimResourceByIdMutation,
  useTapQuarryMutation,
} from '@api/queries/quarry';
import { AppRoute } from '@common/app/routes';
import { defaultToastConfig } from '@common/config/toast';
import { gameItemOrder, resourceList } from '@common/constants';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { useDisabledDelay } from '@common/hooks/disabled-delay';
import { useIntroState } from '@common/hooks/intro-ref';
import { useTapRequestComplete } from '@common/hooks/tap-request-complete';
import { firebaseLogCollectResource } from '@common/lib/firebaseLog';
import { toCapitalizeStr } from '@common/lib/toCapitalizeStr';
import { resourceImages } from '@components/helpers/imagesImports';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { ModalContext } from '@components/Modals/Modal/config';
import { BoostQuarryButton } from '@components/Resources/BoostQuarryButton/BoostQuarryButton';
import { ResourceProgress } from '@components/Resources/ResourceProgress/ResourceProgress';
import { ResourcesNavigation } from '@components/Resources/ResourcesNavigation/ResourcesNavigation';
import { ResourceTitle } from '@components/ResourceTitle/ResourceTitle';
import { ToUpgrade } from '@components/ToUpgrade/ToUpgrade';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { MainButton } from '@components/ui/Button/Button';
import { useModal } from '@context/ModalContext';
import MessageQueue from '@pages/Resources/mineResource/MessageQueue';
import {
  boostQuarryTimersEnd,
  selectQuarriesAsObject,
  selectQuarryByIdOrName,
} from '@redux/selectors/quarrySelectors';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';

import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { useCountDownBoostsTimers } from '@/common/hooks/count-down-boosts-timers';
import { intervalCheckPromise } from '@/common/lib/interval-check-promise';
import { playSound } from '@/common/lib/soundEffect';
import { ResotreEnergyProvider } from '@/components/Modals/RestoreEnergy/provider';
import { MineResourceInfo } from '@/components/Resources/MineResourceInfo/MineResourceInfo';
import { ClaimResourceAnimation } from '@/components/ui/ClaimResourceAnimation/ClaimResourceAnimation';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { ClaimAnimationContext } from '@/context/ClaimAnimationContext';
import { MineResourceContext } from '@/context/MineResourceContext';

import ImgTapping from './ImgTapping';

import css from './styles.module.scss';

const ResourceMine: React.FC = () => {
  const { id } = useParams();
  useGetUserQuarriesForClaimQuery(undefined);
  const [claimResource, { isLoading }] = useClaimResourceByIdMutation();
  const { t } = useTranslation();
  const { showAnimation } = useIntroState(6);
  const ref = useHighlightComponentRect<HTMLButtonElement>(7);
  const { openModal } = useContext(ModalContext);
  const { tapRef } = useContext(MineResourceContext);
  const quarry = useAppSelector((state) => selectQuarryByIdOrName(state, id));
  const buildsData = useAppSelector(selectQuarriesAsObject);
  const [disabled, setIsDisabled] = useDisabledDelay();
  const nextToBuild = useAppSelector(
    (state) => state.quarry.quarries?.nextToBuild,
  );
  const { claimModal } = useModal();
  const { isComplete, isExist } = useCheckRequirements(
    quarry?.nextLevelRequirements,
  );
  const { tapRequestState, setTapRequestState } = useTapRequestComplete(
    quarry?.id,
  );
  const storage = useAppSelector((state) => state.user.user?.storage);
  const previousValueRef = useRef(storage);
  const isTimersEnd = useAppSelector(boostQuarryTimersEnd);
  useCountDownBoostsTimers(!isTimersEnd, 'Quarry');
  const { setIsAnimationStart, isAnimationStart } = useContext(
    ClaimAnimationContext,
  );
  const [tapQuarry, tapQuarryRequestState] = useTapQuarryMutation({
    fixedCacheKey: 'shared-tap-quarry',
  });
  const checkStockOverflow = useCheckStorageOverflow();

  useEffect(() => {
    if (previousValueRef.current !== storage && quarry) {
      firebaseLogCollectResource(quarry, storage, previousValueRef.current);
      previousValueRef.current = storage;
    }
  }, [storage]);

  const onClaimResource = async (): Promise<void> => {
    try {
      if (!quarry?.id) {
        return;
      }
      setIsDisabled(true);
      await intervalCheckPromise({
        checkedValue: !tapQuarryRequestState.isLoading,
        ms: 1000,
      });

      if (tapRef.current && tapRef.current[quarry.id]) {
        const boosts = tapRef.current[quarry.id];
        await tapQuarry({ quarryId: quarry.id, boosts })
          .unwrap()
          .then()
          .catch(() => {
            ToastNotification(t('Something went wrong, please try again'));
          });
      }
      const { storage, plant } = await claimResource({
        id: quarry.id,
      }).unwrap();

      if (plant.id === id) {
        setIsAnimationStart(true);
      }
      setTapRequestState((state) => ({ ...state, isCompleted: false }));
      checkStockOverflow(storage, gameItemOrder.resource);
    } catch (error: unknown) {
      console.log(error);
      showToast(t('Error: Failed to send resources'), undefined, {
        ...defaultToastConfig,
        className: 'justify-center',
      });
    }
  };

  if (!quarry) {
    return null;
  }

  const bg = resourceImages[quarry.name].background;

  const onBoost = (): void => {
    openModal({
      renderKind: 'boostFactory',
      props: {
        plantType: 'Quarry',
        plantId: quarry.id,
        plant: quarry.name,
        item: quarry.name,
        level: quarry.level,
      },
    });
  };

  return (
    quarry && (
      <ResotreEnergyProvider>
        <Helmet>
          <title>Resource_Mine</title>
        </Helmet>

        <main
          id="modalMine"
          className="w-full h-full flex flex-col overflow-y-auto"
        >
          <PageBackground
            className="absolute top-0 left-0 h-[79.27vh] z-[-1] dark-gradient"
            backgroundImage={{
              png: bg.jpg ?? '',
              webp: bg.webp ?? '',
            }}
          />
          <div className={css.mineContent}>
            <div className="w-full relative">
              <ResourceTitle className="mb-1">
                {t(toCapitalizeStr(quarry.name))}
              </ResourceTitle>
              <ResourceProgress
                resourceKind={quarry.name}
                production={quarry.productionRateOriginal}
                stock={quarry.localStock}
                localStockUpdatedAt={quarry.localStockUpdatedAt}
                capacity={quarry.storageCapacity}
                updatedAt={quarry.updatedAt}
                plantId={quarry.id}
              />
              <ClaimResourceAnimation resourceKind={quarry.name} />
            </div>
            <ImgTapping
              id={quarry.id}
              name={quarry.name}
              img={resourceImages[quarry.name].mine}
              tap={quarry.resourcesPerTap || 1}
              claimModal={claimModal}
              disabled={tapRequestState.isLoading}
            />
            <div className="w-full mt-auto">
              <MineResourceInfo
                resourcePerTap={quarry.resourcesPerTap}
                resourceName={quarry.name}
              />
              <div className="w-full flex items-center gap-4 mb-6">
                {isExist && (
                  <ToUpgrade
                    isComplete={isComplete}
                    link={AppRoute.RESOURCES + `/${quarry.id}/upgrade`}
                  />
                )}
                <MainButton
                  ref={ref}
                  id="intro-step-7"
                  onClick={() => {
                    playSound(`collect/${quarry.name}`);
                    setTapRequestState({
                      isLoading: true,
                      isStarted: true,
                      isCompleted: false,
                    });
                    onClaimResource();
                  }}
                  isLoading={isLoading || tapRequestState.isLoading}
                  disabled={isLoading || disabled || tapRequestState.isLoading}
                  inactive={disabled}
                  className={cn(
                    'flex-1',
                    showAnimation && 'relative pointer-events-auto z-[11]',
                  )}
                  text={t('Claim')}
                ></MainButton>
                <BoostQuarryButton onClick={onBoost} plantId={quarry.id} />
              </div>
            </div>
          </div>
          <ResourcesNavigation
            data={{ list: resourceList, nextToBuild, buildsData }}
            isAnimationStart={isAnimationStart}
          />
        </main>
        <MessageQueue />
      </ResotreEnergyProvider>
    )
  );
};

export default ResourceMine;
